import { BaseService } from "./BaseService";
import type {
  DeletionJob,
  DeletionJobsQuery,
  PaginatedDeletionJobs,
  StartDeletionDto,
} from "@/types/data-deletion";

export interface IDataDeletionService {
  startUserDeletion(
    userId: string,
    dto: StartDeletionDto,
  ): Promise<DeletionJob>;
  startGuestDeletion(
    guestId: string,
    dto: StartDeletionDto,
  ): Promise<DeletionJob>;
  startHotelDeletion(
    hotelId: string,
    dto: StartDeletionDto,
  ): Promise<DeletionJob>;
  startTenantDeletion(
    tenantId: string,
    dto: StartDeletionDto,
  ): Promise<DeletionJob>;
  getJob(jobId: string): Promise<DeletionJob>;
  getJobs(query?: DeletionJobsQuery): Promise<PaginatedDeletionJobs>;
  cancelJob(jobId: string): Promise<DeletionJob>;
}

/**
 * SUPER_ADMIN-only GDPR erasure API. All routes live under
 * `/admin/data-deletion` (the configured base URL already includes `/api`).
 *
 * Start/cancel calls suppress the global error toast (`disableToast`) so the
 * destructive flow can surface 400/409 errors inline; the polling reads do the
 * same to avoid toast spam while a job is in flight.
 */
class DataDeletionService extends BaseService implements IDataDeletionService {
  private readonly basePath = "/admin/data-deletion";

  startUserDeletion(
    userId: string,
    dto: StartDeletionDto,
  ): Promise<DeletionJob> {
    return this.request<DeletionJob>(`${this.basePath}/users/${userId}`, {
      method: "POST",
      body: JSON.stringify(dto),
      skipSelectedHotelHeader: true,
      disableToast: true,
    });
  }

  startGuestDeletion(
    guestId: string,
    dto: StartDeletionDto,
  ): Promise<DeletionJob> {
    return this.request<DeletionJob>(`${this.basePath}/guests/${guestId}`, {
      method: "POST",
      body: JSON.stringify(dto),
      // SUPER_ADMIN calls carry `tenantId` and are platform-scoped (skip the
      // hotel header); HOTEL_ADMIN anonymize calls omit it and are hotel-scoped,
      // so let `x-hotel-id` through.
      skipSelectedHotelHeader: Boolean(dto.tenantId),
      disableToast: true,
    });
  }

  startHotelDeletion(
    hotelId: string,
    dto: StartDeletionDto,
  ): Promise<DeletionJob> {
    return this.request<DeletionJob>(`${this.basePath}/hotels/${hotelId}`, {
      method: "POST",
      body: JSON.stringify(dto),
      skipSelectedHotelHeader: true,
      disableToast: true,
    });
  }

  startTenantDeletion(
    tenantId: string,
    dto: StartDeletionDto,
  ): Promise<DeletionJob> {
    return this.request<DeletionJob>(`${this.basePath}/tenants/${tenantId}`, {
      method: "POST",
      body: JSON.stringify(dto),
      skipSelectedHotelHeader: true,
      disableToast: true,
    });
  }

  getJob(jobId: string): Promise<DeletionJob> {
    return this.get<DeletionJob>(`${this.basePath}/jobs/${jobId}`, undefined, {
      skipSelectedHotelHeader: true,
      disableToast: true,
    });
  }

  getJobs(query?: DeletionJobsQuery): Promise<PaginatedDeletionJobs> {
    return this.get<PaginatedDeletionJobs>(`${this.basePath}/jobs`, query, {
      skipSelectedHotelHeader: true,
    });
  }

  cancelJob(jobId: string): Promise<DeletionJob> {
    return this.request<DeletionJob>(`${this.basePath}/jobs/${jobId}/cancel`, {
      method: "POST",
      skipSelectedHotelHeader: true,
      disableToast: true,
    });
  }
}

export const dataDeletionService = new DataDeletionService();
